import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { AdvisorIntentClassification, SolutionEngineDiagnosis } from "@/lib/solutionEngine";

export type AdvisorConversationEntry = {
  role: "user" | "advisor_question";
  content: string;
};

export interface AdvisorSession {
  id: string;
  intent: string;
  modeKey: string;
  query: string;
  diagnosis: SolutionEngineDiagnosis;
  advisorIntent?: AdvisorIntentClassification;
  conversation?: AdvisorConversationEntry[];
  timestamp: number;
  saved?: boolean;
}

interface AdvisorSessionContextValue {
  sessions: Record<string, AdvisorSession>;
  activeSessionId: string | null;
  saveSession: (session: AdvisorSession) => void;
  getSession: (id: string) => AdvisorSession | null;
  markSaved: (id: string) => void;
  clearSession: (id: string) => void;
  clearAll: () => void;
  savedSessions: AdvisorSession[];
}

const STORAGE_KEY = "seatech_advisor_sessions";
const MAX_SESSIONS = 10;

function loadSessions(): Record<string, AdvisorSession> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistSessions(sessions: Record<string, AdvisorSession>) {
  try {
    // Keep only the latest MAX_SESSIONS entries
    const entries = Object.entries(sessions)
      .sort(([, a], [, b]) => b.timestamp - a.timestamp)
      .slice(0, MAX_SESSIONS);
    const trimmed = Object.fromEntries(entries);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    return trimmed;
  } catch {
    return sessions;
  }
}

const AdvisorSessionContext = createContext<AdvisorSessionContextValue | null>(null);

export function AdvisorSessionProvider({ children }: { children: ReactNode }) {
  const [sessions, setSessions] = useState<Record<string, AdvisorSession>>(loadSessions);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const saveSession = useCallback((session: AdvisorSession) => {
    setSessions((prev) => {
      const next = { ...prev, [session.id]: session };
      const trimmed = persistSessions(next);
      return trimmed;
    });
    setActiveSessionId(session.id);
  }, []);

  const getSession = useCallback((id: string): AdvisorSession | null => {
    // Try React state first
    if (sessions[id]) return sessions[id];
    // Fallback: re-read localStorage in case React state hasn't synced yet
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as Record<string, AdvisorSession>;
        if (stored[id]) {
          // Hydrate into React state so subsequent reads are instant
          setSessions((prev) => ({ ...prev, [id]: stored[id] }));
          return stored[id];
        }
      }
    } catch { /* ignore */ }
    return null;
  }, [sessions]);

  const markSaved = useCallback((id: string) => {
    setSessions((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev, [id]: { ...prev[id], saved: true } };
      persistSessions(next);
      return next;
    });
  }, []);

  const clearSession = useCallback((id: string) => {
    setSessions((prev) => {
      const next = { ...prev };
      delete next[id];
      persistSessions(next);
      return next;
    });
    setActiveSessionId((prev) => (prev === id ? null : prev));
  }, []);

  const clearAll = useCallback(() => {
    setSessions({});
    setActiveSessionId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const savedSessions = Object.values(sessions)
    .filter((s) => s.saved)
    .sort((a, b) => b.timestamp - a.timestamp);

  return (
    <AdvisorSessionContext.Provider value={{ sessions, activeSessionId, saveSession, getSession, markSaved, clearSession, clearAll, savedSessions }}>
      {children}
    </AdvisorSessionContext.Provider>
  );
}

export function useAdvisorSession() {
  const ctx = useContext(AdvisorSessionContext);
  if (!ctx) throw new Error("useAdvisorSession must be used within AdvisorSessionProvider");
  return ctx;
}
